import { useState } from "react";
import { Send, MessageCircle, MapPin, Mail, Phone } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { SERVICES, waLink, DEFAULT_WA_MSG, PHONE_DISPLAY, EMAIL, LANDLINES } from "../data/content";

export const QuoteForm = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState(SERVICES[0].title);
  const [details, setDetails] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const msg =
      `Olá, Gold Service! Vim pelo site e quero um orçamento.\n\n` +
      `*Nome:* ${name}\n*Telefone:* ${phone}\n*Serviço:* ${service}` +
      (details ? `\n*Detalhes:* ${details}` : "");
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-black/50 px-4 py-3.5 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none transition-colors duration-300 focus:border-gold/70";

  return (
    <section id="orcamento" className="relative py-16 sm:py-24 lg:py-32 bg-coal/40 overflow-hidden">
      <div className="absolute -top-24 right-0 h-[360px] w-[360px] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
        <Reveal className="lg:col-span-5">
          <Overline>Orçamento rápido</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-balance">
            Peça seu orçamento <span className="gold-text">em 1 minuto</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base leading-relaxed text-mist">
            Preencha ao lado e a mensagem chega pronta no nosso WhatsApp.
            Respondemos rápido, todos os dias.
          </p>
          <a
            data-testid="quote-address-link"
            href="https://www.google.com/maps/search/?api=1&query=Rua+Japur%C3%A1,+570+-+Amazonas,+Contagem+-+MG"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-2 text-sm text-mist hover:text-gold-light transition-colors"
          >
            <MapPin className="h-4 w-4 text-gold shrink-0" />
            Rua Japurá, 570 — Amazonas, Contagem/MG
          </a>
          <a
            data-testid="quote-direct-whatsapp"
            href={waLink(DEFAULT_WA_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-gold-light transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            Prefere falar direto? Chame no {PHONE_DISPLAY}
          </a>
          <a
            data-testid="quote-email-link"
            href={`mailto:${EMAIL}?subject=Pedido%20de%20or%C3%A7amento%20-%20Site%20Gold%20Service`}
            className="mt-3 inline-flex items-center gap-2 text-sm text-mist hover:text-gold-light transition-colors"
          >
            <Mail className="h-4 w-4 text-gold shrink-0" />
            {EMAIL}
          </a>
          <div className="mt-3 flex flex-col gap-1.5">
            {LANDLINES.map((l, i) => (
              <a
                key={l.tel}
                data-testid={`quote-landline-${i}`}
                href={`tel:${l.tel}`}
                className="inline-flex items-center gap-2 text-sm text-mist hover:text-gold-light transition-colors"
              >
                <Phone className="h-4 w-4 text-gold shrink-0" />
                Fixo {l.display}
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.15}>
          <form
            data-testid="quote-form"
            onSubmit={submit}
            className="gold-border-gradient rounded-3xl bg-card p-5 sm:p-8 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.18em] text-mist">
                  Seu nome
                </label>
                <input
                  data-testid="quote-form-name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Maria Silva"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.18em] text-mist">
                  WhatsApp / telefone
                </label>
                <input
                  data-testid="quote-form-phone-input"
                  required
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(31) 99999-0000"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.18em] text-mist">
                Serviço desejado
              </label>
              <select
                data-testid="quote-form-service-select"
                value={service}
                onChange={(e) => setService(e.target.value)}
                className={`${inputCls} appearance-none`}
              >
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title} className="bg-coal">
                    {s.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.18em] text-mist">
                Detalhes (opcional)
              </label>
              <textarea
                data-testid="quote-form-details-input"
                rows={3}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Ex.: apartamento de 80m², estou vendo baratas na cozinha..."
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="quote-form-submit-button"
              type="submit"
              className="group w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-gold py-4 font-bold text-ink text-sm sm:text-base transition-all duration-300 hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] hover:-translate-y-0.5"
            >
              <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              Enviar pedido pelo WhatsApp
            </button>
            <p className="text-center text-[11px] text-zinc-500">
              Ao enviar, o WhatsApp abre com a mensagem pronta. Nada é cobrado.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
